import React, { ElementType } from 'react';

export interface ErpColProps {
  cols?: string | number;
  sm?: string | number | boolean;
  md?: string | number | boolean;
  lg?: string | number | boolean;
  xl?: string | number | boolean;
  xxl?: string | number | boolean;
  offset?: string | number;
  order?: string | number;
  tag?: ElementType | string;
  className?: string;
  children?: React.ReactNode;
}

const ErpCol: React.FC<ErpColProps> = ({
  cols,
  sm,
  md,
  lg,
  xl,
  xxl,
  offset,
  order,
  tag: Tag = 'div',
  className = '',
  children,
}) => {
  const out: string[] = [];

  const push = (prop: string | number | boolean | undefined, breakpoint?: string) => {
    if (prop === undefined || prop === false) return;

    const spanClass = prop === true ? 'col-auto' : `col-span-${prop}`;

    if (breakpoint) {
      out.push(`${breakpoint}:${spanClass}`);
    } else {
      out.push(spanClass);
    }
  };

  // Breakpoint spans must retain a full-width base below that breakpoint.
  // Otherwise CSS grid auto-placement puts a mobile field in one of 12 tracks.
  push(cols ?? 12);
  push(sm, 'sm');
  push(md, 'md');
  push(lg, 'lg');
  push(xl, 'xl');
  push(xxl, 'xxl');

  // Tailwind only generates col-start-1..13 / the order-* allowlist, so an
  // out-of-range or non-numeric value must not be interpolated verbatim.
  if (offset !== undefined && Number.isFinite(Number(offset))) {
    const start = Number(offset) + 1;
    if (start >= 1 && start <= 13) out.push(`col-start-${start}`);
  }
  if (
    order !== undefined &&
    (/^(1[0-2]|[1-9])$/.test(String(order)) || order === 'first' || order === 'last' || order === 'none')
  ) {
    out.push(`order-${order}`);
  }

  if (out.length === 0) out.push('col-span-12');

  const finalClassName = `min-w-0 max-w-full ${out.join(' ')} ${className}`.trim();

  return <Tag className={finalClassName}>{children}</Tag>;
};

export default ErpCol;
